import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { stripeApi } from '@/lib/api/stripe';
import { toast } from 'sonner';

export interface StripeConnectOnboardingProps {
  // Optional callback when onboarding is completed
  onCompleted?: () => void;
}

export const StripeConnectOnboarding = ({ onCompleted }: StripeConnectOnboardingProps) => {
  const [loading, setLoading] = useState(true);
  const [creatingLink, setCreatingLink] = useState(false);
  const [accountStatus, setAccountStatus] = useState<{
    accountId?: string;
    isOnboarded?: boolean;
    details_submitted?: boolean;
    payouts_enabled?: boolean;
    charges_enabled?: boolean;
  }>({});

  const fetchAccountStatus = useCallback(async () => {
    try {
      setLoading(true);
      const status = await stripeApi.getAccountStatus();
      setAccountStatus(status);
      
      if (status.isOnboarded && onCompleted) {
        onCompleted();
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
      toast.error('Failed to get account status');
    } finally {
      setLoading(false);
    }
  }, [onCompleted]);

  useEffect(() => {
    fetchAccountStatus();
  }, [fetchAccountStatus]);

  const handleCreateAccount = async () => {
    try {
      setCreatingLink(true);
      // First create the account if it doesn't exist
      if (!accountStatus.accountId) {
        await stripeApi.createConnectAccount();
      }
      
      // Get the current URL for return and refresh URLs
      const origin = window.location.origin;
      const path = window.location.pathname;
      
      // Create an account link for onboarding
      const result = await stripeApi.createAccountLink(
        `${origin}${path}?refresh=true`, // Refresh URL if the user abandons onboarding
        `${origin}${path}?success=true`  // Return URL when onboarding is complete
      );
      
      // Redirect to Stripe
      window.location.href = result.url;
    } catch (error) {
      console.error('Error creating onboarding link:', error);
      toast.error('Failed to create onboarding link');
      setCreatingLink(false);
    }
  };

  // Check if onboarding was completed (URL param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      // Refresh the account status
      fetchAccountStatus();
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.success('Stripe account setup completed!');
    } else if (params.get('refresh') === 'true') {
      // User abandoned the flow, refresh the status
      fetchAccountStatus();
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchAccountStatus]);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex justify-center items-center h-40">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-4">
      <h2 className="text-2xl font-semibold">Stripe Connect Setup</h2>
      
      {accountStatus.isOnboarded ? (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-md border border-green-200">
            <p className="text-green-800 font-medium">
              Your Stripe Connect account is set up and ready to accept payments!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <h3 className="font-medium mb-2">Account Details</h3>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${accountStatus.details_submitted ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>Account details submitted</span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <h3 className="font-medium mb-2">Payments</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${accountStatus.charges_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span>Can accept payments</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${accountStatus.payouts_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span>Can receive payouts</span>
                </div>
              </div>
            </div>
          </div>
          
          <Button variant="outline" onClick={fetchAccountStatus}>
            Refresh Status
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600">
            To receive payments, you need to set up your Stripe Connect account. This allows you to receive payments directly from buyers through our platform.
          </p>
          
          {accountStatus.accountId && !accountStatus.details_submitted && (
            <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
              <p className="text-yellow-800">
                You've started the onboarding process but haven't completed it yet. Continue setting up your account to start receiving payments.
              </p>
            </div>
          )}
          
          <Button 
            onClick={handleCreateAccount} 
            disabled={creatingLink}
            className="w-full md:w-auto"
          >
            {creatingLink ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                <span>Preparing...</span>
              </>
            ) : accountStatus.accountId ? 'Continue Stripe Setup' : 'Set Up Stripe Account'}
          </Button>
        </div>
      )}
    </Card>
  );
}; 